<template>
  <component
    :is="containerTag"
    v-if="category"
    :class="{
      'product-category-tile-fi': true,
      'product-category-tile-fi--teaser': category.Slug,
      'product-category-tile-fi--is-family-page-variant': isGuidedRecommandation,
      'product-category-tile-fi--has-badge': isGuidedRecommandation && category.familyProductCount,
    }"
    :href="categoryHref"
    @click.stop.prevent="goToCategory"
  >
    <div class="product-category-tile-fi__image">
      <ssr-cloud-image-fi
        v-if="category.image"
        class="product-category-tile-fi__img"
        :src="category.image"
        :alt="category.name || undefined"
        :breakpoints="breakpoints"
        :unlimited="unlimited"
        :aspect-ratio="16 / 9"
        trim
        params="func=fit"
      />
    </div>
    <template v-if="!isGuidedRecommandation">
      <div class="product-category-tile-fi__info">
        <div
          v-if="category.name"
          class="product-category-tile-fi__name"
        >
          {{ category.name }}
        </div>
        <div
          v-if="category.marketingClaim"
          class="product-category-tile-fi__claim"
        >
          {{ category.marketingClaim }}
        </div>
      </div>

      <div
        v-if="category.Slug"
        :class="{
          'fi-text-21': !isLG,
          'fi-text-24': isLG,
          'product-category-tile-fi__arrow': true,
          'fi-semibold': true,
        }"
      >
        <arrow-icon class="product-category-tile-fi__arrow-icon" />
      </div>
    </template>
    <template v-else>
      <div class="product-category-tile-fi__title">
        {{ category.name }}
      </div>
      <div
        v-if="category.familyProductCount"
        class="product-category-tile-fi__badge"
      >
        {{
          $dict.get(
            String(category.familyProductCount) === '1'
              ? 'search.Variants-Singular'
              : 'search.Variants-Plural',
            [String(category.familyProductCount)],
          )
        }}
      </div>
    </template>
  </component>
</template>

<script setup lang="ts">
import { ArrowIcon, SsrCloudImageFi } from 'atoms';
import { computed } from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import type { GetProductCategoryListByIdQuery } from '@/generated/graphql';
import { useNotificationStore } from '@/store/pinia/notification';

type CategoryItemComplete = NonNullable<
  NonNullable<GetProductCategoryListByIdQuery['getProductCategoryListById']>[number]
>;
type CategoryItem = Pick<
  CategoryItemComplete,
  'familyProductCount' | 'name' | 'image' | 'Slug' | 'marketingClaim'
>;

defineOptions({ name: 'ProductCategoryTileFi' });

const props = withDefaults(
  defineProps<{
    category?: CategoryItem | undefined | null;
    isGuidedRecommandation?: boolean;
  }>(),
  {
    category: undefined,
    isGuidedRecommandation: false,
  },
);

const emit = defineEmits<{
  'tile-clicked': [];
}>();

const { isLG } = useBreakpoints();
const fischer = useFischer();
const { openGeneralErrorNotification } = useNotificationStore();

const breakpoints = [
  { mediaWidth: 480, imageWidth: 331 },
  { mediaWidth: 767, imageWidth: 404 },
  { mediaWidth: 768, imageWidth: 256 },
  { mediaWidth: 1023, imageWidth: 361 },
  { mediaWidth: 1440, imageWidth: 300 },
  { mediaWidth: 1920, imageWidth: 300 },
];
const unlimited = 300;

const containerTag = computed(() =>
  props.category?.Slug && !fischer.app.isEditMode() ? 'a' : 'div',
);

const categoryHref = computed(() =>
  props.category?.Slug && !fischer.app.isEditMode()
    ? fischer.pages.products().url + props.category.Slug
    : null,
);

const goToCategory = () => {
  emit('tile-clicked');

  if (props.isGuidedRecommandation) {
    try {
      localStorage.setItem(
        'recommendationClick',
        JSON.stringify({
          refererId: fischer.catalogId(),
          guided: true,
        }),
      );
    } catch (error) {
      console.debug('failed to store recommendation click', error);
    }
  }

  if (categoryHref.value) {
    window.location.href = categoryHref.value;
  } else {
    console.error('Could not redirect to category', categoryHref);
    openGeneralErrorNotification();
  }
};
</script>

<style scoped src="./product-category-tile-fi" />
